/**
 * DesignService - cloud save/open/version state (CLOUD_SYNC_SPEC.md §7)
 *
 * Owns the "current cloud design" so ⌘S knows update-vs-create, tracks
 * dirty state for the title bar, and pushes the debounced working draft
 * (autosave lane - never a version, D2).
 */
import { track } from './Analytics.js';

export class DesignService {
    /**
     * @param {import('./CloudClient.js').CloudClient} client
     * @param {Object} app - Application (serializeDesign/loadDesign/thumbnail)
     */
    constructor(client, app) {
        this.client = client;
        this.app = app;

        // Current cloud identity of the open design (null = local-only)
        this.currentDesignId = null;
        this.currentName = null;
        this.currentComment = null;
        this.currentVersion = 0;   // latest saved version number
        this.dirty = false;        // edits since last cloud Save
        this._draftTimer = null;
        this._onState = new Set();
    }

    onStateChange(fn) {
        this._onState.add(fn);
        return () => this._onState.delete(fn);
    }

    _emit() {
        for (const fn of this._onState) {
            try { fn(this); } catch (e) { console.error(e); }
        }
        // Survive reloads: the local autosave restores the content, this
        // remembers which cloud design it belongs to. Only written when a
        // design is attached - clearing happens explicitly in
        // resetToLocal()/remove(), NOT here (early markDirty calls run
        // before restoreIdentity and must not wipe the stored id).
        if (this.currentDesignId) {
            try {
                localStorage.setItem('sketchchair-cloud-design', this.currentDesignId);
            } catch { /* storage unavailable */ }
        }
    }

    _clearStoredIdentity() {
        try { localStorage.removeItem('sketchchair-cloud-design'); } catch { }
    }

    /**
     * Reattach the cloud identity after a reload (the autosaved content is
     * already restored locally; we only recover WHICH design it is).
     * Called once sign-in state is known. Marks dirty - the local autosave
     * may be ahead of the last saved version.
     */
    async restoreIdentity() {
        if (this.currentDesignId) return;
        let id = null;
        try { id = localStorage.getItem('sketchchair-cloud-design'); } catch { }
        if (!id) return;
        try {
            const meta = await this.client.get(`/designs/${id}`);
            this.currentDesignId = id;
            this.currentName = meta.name;
            this.currentComment = meta.comment;
            this.currentVersion = meta.latest_version;
            this.dirty = true;
            this._emit();
        } catch {
            this._clearStoredIdentity();
        }
    }

    /** Reset to a fresh local-only design (File > New) */
    resetToLocal() {
        this._clearStoredIdentity();
        this.currentDesignId = null;
        this.currentName = null;
        this.currentComment = null;
        this.currentVersion = 0;
        this.dirty = false;
        this._emit();
    }

    /** Mark edited since last save (title bar dot; draft autosave lane) */
    markDirty() {
        if (!this.dirty) {
            this.dirty = true;
            this._emit();
        }
    }

    /** The user's designs, newest first */
    async list() {
        const { designs } = await this.client.get('/designs');
        return designs;
    }

    /**
     * Save the current design as a new version of the current cloud design
     * (creating the design row first if this is a fresh one).
     * @param {{name?: string, comment?: string, forkedFrom?: string}} opts
     * @returns {{id: string, version: number}}
     */
    async save(opts = {}) {
        const promise = this._save(opts);
        // Tracked so dependents (Publish dialog) can await an in-flight
        // save instead of reading half-updated version/dirty state
        this.pendingSave = promise.finally(() => {
            if (this.pendingSave === promise) this.pendingSave = null;
        });
        return promise;
    }

    async _save(opts = {}) {
        const blob = this.app.serializeDesign();
        const thumb = this.app.captureThumbnail?.() || null;
        const isNewDesign = !this.currentDesignId;

        if (!this.currentDesignId) {
            const { id } = await this.client.post('/designs', {
                name: opts.name ?? this.currentName,
                comment: opts.comment ?? this.currentComment,
                forked_from: opts.forkedFrom || undefined
            });
            this.currentDesignId = id;
        }

        const payload = { blob, thumb };
        if (opts.name !== undefined) payload.name = opts.name;
        if (opts.comment !== undefined) payload.comment = opts.comment;

        const { version } = await this.client.post(
            `/designs/${this.currentDesignId}/save`, payload);

        if (opts.name !== undefined) this.currentName = opts.name;
        if (opts.comment !== undefined) this.currentComment = opts.comment;
        this.currentVersion = version;
        this.dirty = false;
        this._emit();
        track(opts.forkedFrom ? 'design_fork' : 'design_save',
            { version, new_design: isNewDesign });
        return { id: this.currentDesignId, version };
    }

    /**
     * Save As: always creates a NEW design (fork of a share when
     * forkedFrom is set - D4).
     */
    async saveAs({ name, comment, forkedFrom } = {}) {
        this.currentDesignId = null;
        this.currentVersion = 0;
        this.currentName = name ?? null;
        this.currentComment = comment ?? null;
        return this.save({ name, comment, forkedFrom });
    }

    /** Open a design (latest version unless v is given) into the app */
    async open(id, v = null) {
        const meta = await this.client.get(`/designs/${id}`);
        const version = v ?? meta.latest_version;
        if (version < 1) throw new Error('design has no saved versions');
        const { design } = await this.client.get(`/designs/${id}/versions/${version}`);
        if (!this.app.loadDesign(design)) {
            throw new Error('could not load design (see console)');
        }
        this.currentDesignId = id;
        this.currentName = meta.name;
        this.currentComment = meta.comment;
        this.currentVersion = meta.latest_version;
        this.dirty = version !== meta.latest_version; // opened an old version
        this._emit();
        track('design_open', { version });
        return meta;
    }

    async rename(id, name) {
        await this.client.patch(`/designs/${id}`, { name });
        if (id === this.currentDesignId) {
            this.currentName = name;
            this._emit();
        }
    }

    async remove(id) {
        await this.client.delete(`/designs/${id}`);
        if (id === this.currentDesignId) this.resetToLocal();
        else {
            // Deleting the stored design from the Open dialog while another
            // is current: drop the stale pointer if it matches
            try {
                if (localStorage.getItem('sketchchair-cloud-design') === id) {
                    this._clearStoredIdentity();
                }
            } catch { }
        }
    }

    /**
     * Debounced working-draft push (autosave lane). Called on every local
     * autosave; only runs once signed in AND the design exists in the
     * cloud. Never creates versions (D2). Failures are silent - the
     * localStorage autosave is still the safety net.
     */
    scheduleDraftSave() {
        if (!this.currentDesignId || !this.client.auth.isSignedIn) return;
        clearTimeout(this._draftTimer);
        this._draftTimer = setTimeout(async () => {
            try {
                const blob = this.app.serializeDesign();
                await this.client.put(
                    `/designs/${this.currentDesignId}/draft`, { blob });
            } catch (e) {
                console.warn('draft autosave failed:', e.message);
            }
        }, 2500);
    }

    /** Owner thumbnail object URL (authenticated) or null */
    thumbUrl(id, version) {
        return this.client.blobUrl(`/designs/${id}/versions/${version}/thumb`);
    }
}
